import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CardFilter, EMPTY_FILTER, isFilterActive, type Label } from '@/entities/board';
import { useIsMobile, useIsShortLandscape } from '@/shared/lib/media';
import { Button } from '@/shared/ui';

import styles from './BoardFilterBar.module.css';
import { activeFilterCount } from './count';
import { LabelFilterDropdown, SelectFilters } from './FilterControls';
import { FilterSheet } from './FilterSheet';
import { ActiveFilterChips } from './ActiveFilterChips';
import { SearchBox } from './SearchBox';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  labels: Label[];
  filter: CardFilter;
  /** Receives an updater applied to the latest filter (safe for quick consecutive clicks). */
  onChange: (update: (current: CardFilter) => CardFilter) => void;
  /** Number of matching cards, or null while unknown. */
  matches: number | null;
}

/**
 * Search + filters. Filters change the view only, never the cards. Desktop shows every control
 * inline; on phones the bar is `[search] [Filters · n]` and the rest lives in a bottom sheet.
 */
export function BoardFilterBar({ labels, filter, onChange, matches }: Props) {
  const { t } = useTranslation();
  // The one-row [search][Filters] bar also serves a phone on its side, where height is scarce.
  const phone = useIsMobile();
  const shortLandscape = useIsShortLandscape();
  const mobile = phone || shortLandscape;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [text, setText] = useState(filter.q);
  const [prevQ, setPrevQ] = useState(filter.q);
  if (filter.q !== prevQ) {
    // External change (back/forward, clear): follow the URL.
    setPrevQ(filter.q);
    setText(filter.q);
  }

  useEffect(() => {
    if (text === filter.q) return;
    const timer = setTimeout(() => {
      onChange((current) => ({ ...current, q: text }));
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [text, filter, onChange]);

  const active = isFilterActive({ ...filter, q: text });
  const clearAll = () => {
    setText('');
    onChange(() => EMPTY_FILTER);
  };
  const summary = (
    <span className={styles.summary} role="status">
      {matches === null ? t('common.loading') : t('filter.matches', { count: matches })}
      {' · '}
      {t('filter.dragDisabled')}
    </span>
  );

  if (mobile) {
    const count = activeFilterCount(filter);
    return (
      <div className={styles.mobile} role="search">
        <div className={styles.mobileRow}>
          <SearchBox value={text} onChange={setText} />
          <button
            type="button"
            className={`${styles.filtersButton} ${count > 0 ? styles.filtersButtonOn : ''}`}
            aria-haspopup="dialog"
            onClick={() => {
              setSheetOpen(true);
            }}
          >
            <span aria-hidden="true">⚲</span>
            {count > 0 ? t('filter.filtersCount', { count }) : t('filter.filters')}
          </button>
        </div>
        {count > 0 && <ActiveFilterChips labels={labels} filter={filter} onChange={onChange} />}
        {active && (
          <div className={styles.mobileSummary}>
            {summary}
            <Button size="sm" variant="ghost" onClick={clearAll}>
              {t('filter.clear')}
            </Button>
          </div>
        )}
        {sheetOpen && (
          <FilterSheet
            labels={labels}
            filter={filter}
            onApply={(next) => {
              onChange(() => next);
            }}
            onClose={() => {
              setSheetOpen(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.bar} role="search">
      <SearchBox value={text} onChange={setText} />
      <SelectFilters filter={filter} onChange={onChange} />
      <LabelFilterDropdown labels={labels} filter={filter} onChange={onChange} />
      {active && (
        <>
          {summary}
          <Button size="sm" variant="ghost" onClick={clearAll}>
            {t('filter.clear')}
          </Button>
        </>
      )}
    </div>
  );
}
